import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Calendar, Clock, X, Loader2, CreditCard, Coins } from "lucide-react";
import IntakeAnswersForm, { validateIntakeAnswers } from "./IntakeAnswersForm";

/**
 * Build a 14-day grid of default booking slots when a practitioner has
 * not published their own availability rules. We generate slots from
 * 09:00 to 20:00 local time in `durationMin`-minute increments (rounded
 * up to the next 30-min mark). Day 0 is tomorrow — never today — so
 * every default booking has at least ~one day of notice.
 *
 * Return shape matches the availability API's `days` array so the
 * existing date-strip + slot-grid UI can render it unchanged.
 */
function buildDefaultDays(localTz, durationMin) {
  const step = Math.max(15, Math.min(60, Math.round(durationMin / 15) * 15)) || 30;
  const startHour = 9;
  const endHour = 20;
  const daysOut = [];
  const now = new Date();
  for (let i = 1; i <= 14; i++) {
    const day = new Date(now);
    day.setDate(day.getDate() + i);
    // Compose a `YYYY-MM-DD` string in the LOCAL timezone.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: localTz, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(day);
    const yyyy = parts.find((p) => p.type === "year").value;
    const mm = parts.find((p) => p.type === "month").value;
    const dd = parts.find((p) => p.type === "day").value;
    const dateKey = `${yyyy}-${mm}-${dd}`;
    const slots = [];
    for (let h = startHour * 60; h + durationMin <= endHour * 60; h += step) {
      const hh = String(Math.floor(h / 60)).padStart(2, "0");
      const mn = String(h % 60).padStart(2, "0");
      // Build a Date in local TZ then serialise as UTC ISO. Because the
      // date string is already local-day-key, `new Date("YYYY-MM-DDTHH:MM:00")`
      // interprets it in the browser's local timezone, which matches
      // what we want when the browser's TZ === localTz (the common case).
      const localIso = `${dateKey}T${hh}:${mn}:00`;
      const utcIso = new Date(localIso).toISOString();
      slots.push({ slotAt: utcIso });
    }
    daysOut.push({ date: dateKey, slots });
  }
  return daysOut;
}

/**
 * AvailabilityBookingPicker — Acuity-style booking flow.
 *
 * Renders a "Book Now" button that opens a modal with:
 *   1. A horizontal date strip (next 14 days) — greyed out days have no slots
 *   2. A time-slot grid for the selected date
 *   3. Optional intake-question form (from `intakeQuestions` prop)
 *   4. Card + Crypto payment CTAs, both attached to the picked slot
 *
 * Slots come from GET /providers/{uid}/availability (rules-expanded,
 * booked-slot-filtered). If the provider hasn't published any rules,
 * we render a friendly "Booking directly" fallback so the existing
 * pay-now flow still works.
 */
export default function AvailabilityBookingPicker({
  providerId, serviceId, serviceTitle, durationMin,
  intakeQuestions,
  onPick, onCryptoBook,
}) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState(null);   // {code, percentOff, discount, priceAfter} | null
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const hasIntake = Array.isArray(intakeQuestions) && intakeQuestions.length > 0;

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    setCouponError("");
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.trim(),
        serviceId,
      });
      setCouponPreview(data);
    } catch (e) {
      setCouponPreview(null);
      setCouponError(e.response?.data?.detail || "Invalid code");
    } finally {
      setApplyingCoupon(false);
    }
  };
  const clearCoupon = () => {
    setCouponCode("");
    setCouponPreview(null);
    setCouponError("");
  };

  // Detect the client's IANA timezone once — fallback to "UTC" in exotic
  // environments that don't populate `resolvedOptions().timeZone`.
  const localTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch { return "UTC"; }
  })();
  const tzShort = (() => {
    try {
      const parts = new Intl.DateTimeFormat(undefined, {
        timeZone: localTz, timeZoneName: "short",
      }).formatToParts(new Date());
      return parts.find((p) => p.type === "timeZoneName")?.value || "";
    } catch { return ""; }
  })();
  const fmtSlotTime = (iso) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: "numeric", minute: "2-digit", timeZone: localTz,
      }).format(new Date(iso));
    } catch { return iso.slice(11, 16); }
  };
  const fmtSlotFull = (iso) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", timeZone: localTz,
      }).format(new Date(iso));
    } catch { return iso; }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/providers/${providerId}/availability?days=14`);
        // If the practitioner hasn't published any availability rules
        // OR every returned day is empty, synthesise a default 14-day
        // grid (9am–8pm local time, 30-min increments) so clients ALWAYS
        // have to lock in a specific date + time — even before the
        // practitioner has configured their own rules. The practitioner
        // still gets the booked slot on their dashboard + calendar
        // invite and can move it in a reply.
        const returned = data.days || [];
        const anyRealSlots = returned.some((d) => (d.slots || []).length > 0);
        setDays(anyRealSlots ? returned : buildDefaultDays(localTz, durationMin || 30));
        const initial = anyRealSlots
          ? returned[0]?.date
          : (buildDefaultDays(localTz, durationMin || 30)[0]?.date || null);
        setSelectedDate(initial);
        setSelectedSlot(null);
      } catch {
        // Even on network error we still let the client pick a slot
        // from the default grid so they never hit a dead end.
        const fallback = buildDefaultDays(localTz, durationMin || 30);
        setDays(fallback);
        setSelectedDate(fallback[0]?.date || null);
        setSelectedSlot(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, providerId, durationMin]);

  const selectedDay = days?.find((d) => d.date === selectedDate);

  const confirmCard = () => {
    if (!selectedSlot) return toast.error("Pick a time first");
    if (hasIntake) {
      const missing = validateIntakeAnswers(intakeQuestions, answers);
      if (missing.length) {
        return toast.error(`Please answer: ${missing.join(", ")}`);
      }
    }
    setOpen(false);
    onPick(selectedSlot.slotAt, hasIntake ? answers : undefined, couponPreview?.code || null);
  };
  const confirmCrypto = () => {
    if (!selectedSlot) return toast.error("Pick a time first");
    if (hasIntake) {
      const missing = validateIntakeAnswers(intakeQuestions, answers);
      if (missing.length) {
        return toast.error(`Please answer: ${missing.join(", ")}`);
      }
    }
    setOpen(false);
    onCryptoBook(selectedSlot.slotAt, hasIntake ? answers : undefined, couponPreview?.code || null);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs whitespace-nowrap inline-flex items-center gap-1.5"
        data-testid={`book-service-${serviceId}`}
      >
        <Calendar className="w-3.5 h-3.5" /> Book Now
      </button>
      <div className="text-[10px] uppercase tracking-[0.15em] text-foreground/45">Card · Apple Pay · Google Pay · Crypto</div>

      {open && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`booking-modal-${serviceId}`}>
          <div className="parchment w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 relative">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-foreground/55 hover:text-foreground p-1"
              data-testid="booking-modal-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="mb-5">
              <div className="label-mystic text-secondary">Book a session</div>
              <h2 className="font-serif text-2xl mt-1">{serviceTitle}</h2>
              <p className="text-[11px] text-foreground/55 mt-1">
                {durationMin ? `${durationMin} min · ` : ""}Times shown in your local timezone{tzShort ? ` (${tzShort})` : ""}.
              </p>
            </div>

            {loading || days === null ? (
              <div className="py-16 text-center text-foreground/55"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : (
              <>
                {/* Date strip — use each day's first slot to render the
                    local-timezone weekday/date so we don't cross a day
                    boundary in the user's TZ. */}
                <div className="overflow-x-auto scroll-thin mb-5">
                  <div className="flex gap-2 pb-2">
                    {days.map((d) => {
                      const firstSlotIso = d.slots?.[0]?.slotAt || (d.date + "T12:00:00Z");
                      const dt = new Date(firstSlotIso);
                      const active = d.date === selectedDate;
                      const localWeekday = new Intl.DateTimeFormat(undefined, { weekday: "short", timeZone: localTz }).format(dt);
                      const localDay = new Intl.DateTimeFormat(undefined, { day: "numeric", timeZone: localTz }).format(dt);
                      const localMonth = new Intl.DateTimeFormat(undefined, { month: "short", timeZone: localTz }).format(dt);
                      return (
                        <button
                          key={d.date}
                          onClick={() => { setSelectedDate(d.date); setSelectedSlot(null); }}
                          className={`flex flex-col items-center px-4 py-3 rounded-sm border shrink-0 min-w-[76px] transition ${
                            active
                              ? "bg-secondary text-secondary-foreground border-secondary"
                              : "border-secondary/25 hover:border-secondary/60"
                          }`}
                          data-testid={`booking-date-${d.date}`}
                        >
                          <span className="text-[10px] uppercase tracking-wider opacity-70">{localWeekday}</span>
                          <span className="font-serif text-2xl">{localDay}</span>
                          <span className="text-[10px] opacity-70">{localMonth}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Slot grid */}
                <div className="mb-5">
                  <div className="label-mystic text-secondary mb-3 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" /> Choose a time
                  </div>
                  {!selectedDay || selectedDay.slots.length === 0 ? (
                    <div className="text-sm text-foreground/55 py-4 text-center">No times available on this day.</div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {selectedDay.slots.map((s) => {
                        const active = selectedSlot?.slotAt === s.slotAt;
                        return (
                          <button
                            key={s.slotAt}
                            onClick={() => setSelectedSlot(s)}
                            className={`px-3 py-2.5 rounded-sm text-sm font-mono border transition ${
                              active
                                ? "bg-secondary text-secondary-foreground border-secondary"
                                : "border-secondary/25 hover:border-secondary/60"
                            }`}
                            data-testid={`booking-slot-${s.slotAt}`}
                          >
                            {fmtSlotTime(s.slotAt)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Intake questions — collected before payment. */}
                {hasIntake && (
                  <div className="mb-5 pt-2 border-t border-secondary/15 text-left">
                    <div className="label-mystic text-secondary mb-3 pt-3">A few quick questions</div>
                    <IntakeAnswersForm
                      questions={intakeQuestions}
                      value={answers}
                      onChange={setAnswers}
                    />
                  </div>
                )}

                {/* Coupon code (optional) */}
                <div className="mb-5 pt-2 border-t border-secondary/15 text-left" data-testid="coupon-block">
                  <div className="label-mystic text-secondary mb-2 pt-3">Have a coupon?</div>
                  {couponPreview ? (
                    <div className="flex items-center justify-between px-3 py-2 rounded-sm border border-emerald-600/40 bg-emerald-950/20 text-xs" data-testid="coupon-applied">
                      <span className="text-emerald-300">
                        {couponPreview.code} · {couponPreview.percentOff}% off ·
                        <span className="ml-1 text-emerald-200 font-semibold">−${couponPreview.discount.toFixed(2)}</span>
                      </span>
                      <button
                        onClick={clearCoupon}
                        className="text-emerald-200 hover:text-emerald-50 underline"
                        data-testid="coupon-clear"
                      >Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 px-3 py-2 rounded-sm border border-secondary/25 bg-transparent text-sm uppercase tracking-wider"
                        data-testid="coupon-input"
                      />
                      <button
                        onClick={applyCoupon}
                        disabled={!couponCode.trim() || applyingCoupon}
                        className="px-4 py-2 rounded-sm border border-secondary text-secondary hover:bg-secondary/10 text-xs uppercase tracking-wider disabled:opacity-50"
                        data-testid="coupon-apply"
                      >{applyingCoupon ? "…" : "Apply"}</button>
                    </div>
                  )}
                  {couponError && <div className="text-xs text-destructive mt-1" data-testid="coupon-error">{couponError}</div>}
                </div>

                {/* Confirm */}
                <div className="flex flex-col gap-2 border-t border-secondary/15 pt-4">
                  <button
                    onClick={confirmCard}
                    disabled={!selectedSlot}
                    className="bg-primary text-primary-foreground disabled:opacity-50 py-3 rounded-sm uppercase tracking-wider text-xs font-semibold inline-flex items-center justify-center gap-2"
                    data-testid="booking-confirm-card"
                  >
                    <CreditCard className="w-4 h-4" />
                    {selectedSlot
                      ? `Book ${fmtSlotFull(selectedSlot.slotAt)} — Pay by Card`
                      : "Pay by Card · Apple Pay · Google Pay"}
                  </button>
                  <button
                    onClick={confirmCrypto}
                    className="border border-secondary text-secondary hover:bg-secondary/10 py-2.5 rounded-sm uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2"
                    data-testid={`book-service-crypto-${serviceId}`}
                  >
                    <Coins className="w-4 h-4" /> Pay · Crypto
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
